import { usePluginQueryScope } from "@/pages/plugins/usePluginQueryScope";
import { usePluginWriteAccess } from "@/hooks/usePluginWriteAccess";
import { useOrganization } from "@/contexts/Auth";
import { useRBAC } from "@/hooks/useRBAC";
import { MemberFacepile } from "@/components/member-facepile";
import {
  DetailSidebarInfoLabel,
  DetailSidebarNav,
  type DetailSidebarNavItem,
} from "@/components/detail/detail-sidebar-nav";
import { Badge } from "@/components/ui/Badge";
import { Text } from "@/components/ui/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import {
  activePluginSection,
  pluginSectionHref,
  PLUGIN_ASSIGNMENTS_SECTION_ID,
  PLUGIN_OVERVIEW_SECTION_ID,
  PLUGIN_SETTINGS_SECTION_ID,
  PLUGIN_SKILLS_SECTION_ID,
  type PluginSection,
} from "@/pages/plugins/plugin-detail-sections";
import {
  audienceMapByUrn,
  describePrincipal,
  individualMemberFacepile,
  isIndividualMemberPrincipal,
  memberMapByUrn,
  principalIcon,
  roleMapByUrn,
} from "@/pages/plugins/principals";
import { usePluginAssignmentsVisible } from "@/pages/plugins/use-plugin-assignments-visible";
import { useRoutes } from "@/routes";
import { useMembers } from "@gram/client/react-query/members";
import { useAudiences } from "@gram/client/react-query/audiences";
import { usePlugin } from "@gram/client/react-query/plugin";
import { usePublishStatus } from "@gram/client/react-query/publishStatus";
import { useRoles } from "@gram/client/react-query/roles";
import {
  LayoutDashboard,
  Settings as SettingsIcon,
  Sparkles,
  Users,
} from "lucide-react";
import * as React from "react";
import { useLocation, useParams } from "react-router";

export function PluginDetailSidebarNav(): React.JSX.Element | null {
  const { hasScope, isLoading } = useRBAC();
  const organization = useOrganization();
  const canWritePlugin = usePluginWriteAccess();
  if (isLoading) return null;
  return canWritePlugin || hasScope("org:read", organization.id) ? (
    <OrganizationPluginSidebarNav />
  ) : (
    <DistributionPluginSidebarNav />
  );
}

function DistributionPluginSidebarNav(): React.JSX.Element | null {
  const routes = useRoutes();
  const location = useLocation();
  const { pluginId } = useParams<{ pluginId: string }>();
  if (!pluginId) return null;
  const skillId = new URLSearchParams(location.search).get("skillId");
  return (
    <DetailSidebarNav
      backHref={
        skillId ? routes.skills.detail.href(skillId) : routes.skills.href()
      }
      backLabel={skillId ? "Back to skill" : "Back to skills"}
      itemsTitle="Distribution"
      items={[
        {
          key: PLUGIN_SKILLS_SECTION_ID,
          title: "Skills",
          Icon: Sparkles,
          href: `${pluginSectionHref(routes, pluginId, PLUGIN_SKILLS_SECTION_ID)}${location.search}`,
          active: true,
        },
      ]}
    />
  );
}

function OrganizationPluginSidebarNav(): React.JSX.Element | null {
  const { hasScope } = useRBAC();
  const organization = useOrganization();
  return hasScope("org:admin", organization.id) ? (
    <PluginSidebarWithAssignments />
  ) : (
    <PluginSidebarContent />
  );
}

function PluginSidebarWithAssignments(): React.JSX.Element | null {
  const scope = usePluginQueryScope();
  const { data: membersData } = useMembers(scope, undefined, {
    queryKeyHashFn: (key) => JSON.stringify([scope, key]),
  });
  const { data: rolesData } = useRoles(scope, undefined, {
    queryKeyHashFn: (key) => JSON.stringify([scope, key]),
  });
  const { data: audiencesData } = useAudiences(scope);
  return (
    <PluginSidebarContent
      membersData={membersData}
      rolesData={rolesData}
      audiencesData={audiencesData}
    />
  );
}

function PluginSidebarContent({
  membersData,
  rolesData,
  audiencesData,
}: {
  membersData?: ReturnType<typeof useMembers>["data"];
  rolesData?: ReturnType<typeof useRoles>["data"];
  audiencesData?: ReturnType<typeof useAudiences>["data"];
} = {}): React.JSX.Element | null {
  const { hasScope } = useRBAC();
  const organization = useOrganization();
  const canWritePlugin = usePluginWriteAccess();
  const canAdmin = hasScope("org:admin", organization.id);
  const routes = useRoutes();
  const location = useLocation();
  const { pluginId } = useParams<{ pluginId: string }>();

  const scope = usePluginQueryScope();
  const { data: plugin } = usePlugin(
    { ...scope, id: pluginId ?? "" },
    undefined,
    {
      throwOnError: false,
      enabled: !!pluginId,
    },
  );
  const { data: publishStatus } = usePublishStatus(scope);
  const assignmentsVisible = usePluginAssignmentsVisible();
  const showAssignments = canAdmin && assignmentsVisible;

  const memberByUrn = React.useMemo(
    () => memberMapByUrn(membersData?.members ?? []),
    [membersData?.members],
  );
  const roleByUrn = React.useMemo(
    () => roleMapByUrn(rolesData?.roles ?? []),
    [rolesData?.roles],
  );
  const audienceByUrn = React.useMemo(
    () => audienceMapByUrn(audiencesData?.audiences ?? []),
    [audiencesData?.audiences],
  );

  if (!pluginId) return null;

  const activeSectionId = activePluginSection(location.pathname, pluginId);
  const sectionItem = (
    sectionId: PluginSection,
    title: string,
    Icon: React.ComponentType<{ className?: string }>,
  ): DetailSidebarNavItem => ({
    key: sectionId,
    title,
    Icon,
    href: pluginSectionHref(routes, pluginId, sectionId),
    active: activeSectionId === sectionId,
  });

  const items: DetailSidebarNavItem[] = [
    sectionItem(PLUGIN_OVERVIEW_SECTION_ID, "Overview", LayoutDashboard),
    sectionItem(PLUGIN_SKILLS_SECTION_ID, "Skills", Sparkles),
    ...(showAssignments
      ? [sectionItem(PLUGIN_ASSIGNMENTS_SECTION_ID, "Assignments", Users)]
      : []),
    ...(canWritePlugin
      ? [sectionItem(PLUGIN_SETTINGS_SECTION_ID, "Settings", SettingsIcon)]
      : []),
  ];

  const assignments = plugin?.assignments ?? [];
  const facepileMembers = individualMemberFacepile(assignments, memberByUrn);
  // Everyone/role/email principals have no avatar, so they sit beside the
  // face-stack as icon chips rather than being dropped from the glance.
  const nonMemberAssignments = assignments.filter(
    (a) => !isIndividualMemberPrincipal(a.principalUrn),
  );
  const cleanVersion = publishStatus?.connected
    ? publishStatus.liveVersion?.replace(/[^\w.\-+]/g, "").trim()
    : undefined;
  // Only the explicit booleans are a known freshness — an undefined upToDate
  // (connection predates fingerprinting) must not read as "Up to date".
  const hasUnpublishedChanges = publishStatus?.upToDate === false;
  const isUpToDate = publishStatus?.upToDate === true;
  const showMarketplaceInfo =
    !!publishStatus?.connected &&
    (hasUnpublishedChanges || isUpToDate || !!cleanVersion);
  const serverCount = plugin?.serverCount ?? plugin?.servers?.length ?? 0;

  const cardContent = plugin && (
    <>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <Text className="truncate font-semibold">{plugin.name}</Text>
          {plugin.isDefault && (
            <Badge variant="information">
              <Badge.Text>Default</Badge.Text>
            </Badge>
          )}
        </div>
        <Text variant="small" muted className="truncate font-mono text-xs">
          {plugin.slug}
        </Text>
      </div>

      {showMarketplaceInfo && (
        <div className="flex flex-col gap-1">
          <DetailSidebarInfoLabel>Marketplace</DetailSidebarInfoLabel>
          <div className="flex flex-wrap items-center gap-1.5">
            {hasUnpublishedChanges && (
              <Badge variant="warning">
                <Badge.Text>Needs syncing</Badge.Text>
              </Badge>
            )}
            {isUpToDate && (
              <Badge variant="success">
                <Badge.Text>Up to date</Badge.Text>
              </Badge>
            )}
            {cleanVersion && (
              <Badge variant="information">
                <Badge.Text className="font-mono">{cleanVersion}</Badge.Text>
              </Badge>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-6">
        <div className="flex flex-col gap-1">
          <DetailSidebarInfoLabel>Servers</DetailSidebarInfoLabel>
          <Text variant="small">{serverCount}</Text>
        </div>
        <div className="flex flex-col gap-1">
          <DetailSidebarInfoLabel>Skills</DetailSidebarInfoLabel>
          <Text variant="small">{plugin.skillCount ?? 0}</Text>
        </div>
      </div>

      {showAssignments && (
        <div className="flex flex-col gap-1.5">
          <DetailSidebarInfoLabel>Distributed to</DetailSidebarInfoLabel>
          {assignments.length === 0 ? (
            <Text variant="small" muted className="text-xs">
              No one yet
            </Text>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              {facepileMembers.length > 0 && (
                <MemberFacepile members={facepileMembers} />
              )}
              {nonMemberAssignments.map((assignment) => {
                const { kind, label } = describePrincipal(
                  assignment.principalUrn,
                  roleByUrn,
                  memberByUrn,
                  audienceByUrn,
                );
                const PrincipalIcon = principalIcon(kind);
                return (
                  <Tooltip key={assignment.id}>
                    <TooltipTrigger asChild>
                      <div
                        role="img"
                        aria-label={label}
                        tabIndex={0}
                        className="bg-muted text-muted-foreground ring-background focus-visible:ring-ring flex size-7 shrink-0 items-center justify-center rounded-full ring-2 focus-visible:outline-none"
                      >
                        <PrincipalIcon className="size-3.5" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">{label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <DetailSidebarNav
      backHref={routes.plugins.href()}
      backLabel="Back to all plugins"
      cardContent={cardContent}
      items={items}
    />
  );
}
